import { getQuoteOrOrderSubTotalValue } from "../../util/QuotesAndOrders";
import React, { useEffect } from "react";
import styled from "styled-components/macro";
import type {
  Fee,
  CurrencyCode,
  Payment,
  IDiscountCodesSchema,
  IOrderItem,
  QuoteRequestItem,
  SampleRequestDetail,
} from "../../types/types";
import { formatPrice } from "../../util/util-components";
import { useTranslation } from "react-i18next";
import { DiscountCode } from "../DiscountCodes/DiscountCodes";
import { useFormWrapper } from "../../util/util";

const Container = styled.div`
  max-width: 340px;
  background-color: ${({ theme }) => theme.secondaryBG};
  padding: 0 ${({ theme }) => theme.resolve("spacing_sm")};
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 5px 0;
`;

const ItemText = styled.span`
  color: ${({ theme }) => theme.secondaryTextColor};
  font-family: ${({ theme }) => theme.fontFamily};
  font-size: ${({ theme }) => theme.fontSizes.small};
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.33;
  letter-spacing: normal;
  margin-right: 30px;
`;

const NumberText = styled.span`
  color: ${({ theme }) => theme.primaryTextColor};
  font-family: ${({ theme }) => theme.fontFamily};
  font-size: ${({ theme }) => theme.fontSizes.small};
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.33;
  letter-spacing: normal;
`;

const TotalText = styled.span`
  color: ${({ theme }) => theme.primaryTextColor};
  font-family: ${({ theme }) => theme.fontFamily};
  font-size: ${({ theme }) => theme.fontSizes.large};
  font-weight: 600;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.33;
  letter-spacing: normal;
  margin-right: 30px;
`;

const TotalValue = styled(TotalText)`
  margin-right: 0;
`;

const HorizontalSeparator = styled.ol`
  border-bottom: ${({ theme }) => theme.resolve("border_md")} solid
    ${({ theme }) => theme.secondaryBorder};
  padding-left: 0;
  list-style: none;
  display: flex;
`;

type CalculatePriceDetailsProps = {
  items: QuoteRequestItem[] | IOrderItem[] | SampleRequestDetail[];
  fees: Fee[];
  currencyCode: CurrencyCode;
  totalDiscount?: string;
};

/**
 * Calculate the price details for a quote or order.
 * The return values can be passed as props to the PriceDetails component.
 * We use this separate calculation function so the values can be accessible
 * in the parent components, which is sometimes needed.
 */
export const calculatePriceDetails = ({
  items,
  fees,
  currencyCode,
  totalDiscount,
}: CalculatePriceDetailsProps) => {
  const subtotal = getQuoteOrOrderSubTotalValue(items);

  const feesTotal = fees.reduce(
    (total, fee) => total + (Number(fee.amount) || 0),
    0
  );

  const total =
    subtotal === null || isNaN(feesTotal)
      ? null
      : subtotal + feesTotal - (Number(totalDiscount) || 0);

  const subtotalString =
    subtotal === null ? "--" : formatPrice(subtotal, currencyCode);

  const totalAfter = !total || total > 0 ? total : 0;
  const totalString =
    totalAfter != null ? formatPrice(totalAfter, currencyCode) : "--";

  return {
    subtotal,
    feesTotal,
    total,
    subtotalString,
    totalString,
  };
};

/**
 * display payments object as line items. Either display the estimated amounts
 * or the collected amounts but never a combination. If the data isn't in the
 * shape we expect display an error.
 */
export function StripePayments({
  payment,
  currencyCode,
}: {
  payment: Partial<Payment>;
  currencyCode: CurrencyCode;
}) {
  const { t } = useTranslation();

  if (payment.agilis_fee && payment.stripe_fee && payment.payment_net) {
    const agilisFee = formatPrice(Number(payment.agilis_fee), currencyCode);
    const stripeFee = formatPrice(Number(payment.stripe_fee), currencyCode);
    const net = formatPrice(Number(payment.payment_net), currencyCode);
    return (
      <>
        <Row>
          <ItemText>{t("Payment Processing Fee")}</ItemText>
          <NumberText>
            {isNaN(Number(payment.stripe_fee)) ? t("N/A") : stripeFee}
          </NumberText>
        </Row>
        <Row>
          <ItemText>{t("Platform Fee")}</ItemText>
          <NumberText>
            {isNaN(Number(payment.agilis_fee)) ? t("N/A") : agilisFee}
          </NumberText>
        </Row>
        <Row>
          <ItemText>{t("Net")}</ItemText>
          <NumberText>
            {isNaN(Number(payment.payment_net)) ? t("N/A") : net}
          </NumberText>
        </Row>
      </>
    );
  } else if (
    payment.estimated_agilis_fee &&
    payment.estimated_stripe_fee &&
    payment.estimated_net
  ) {
    const estimatedAgilisFee = formatPrice(
      Number(payment.estimated_agilis_fee),
      currencyCode
    );
    const estimatedStripeFee = formatPrice(
      Number(payment.estimated_stripe_fee),
      currencyCode
    );
    const estimatedNet = formatPrice(
      Number(payment.estimated_net),
      currencyCode
    );

    return (
      <>
        <Row>
          <ItemText>{t("Est. Payment Processing Fee")}</ItemText>
          <NumberText>
            {isNaN(Number(payment.estimated_stripe_fee))
              ? t("N/A")
              : estimatedStripeFee}
          </NumberText>
        </Row>
        <Row>
          <ItemText>{t("Est. Platform Fee")}</ItemText>
          <NumberText>
            {isNaN(Number(payment.estimated_agilis_fee))
              ? t("N/A")
              : estimatedAgilisFee}
          </NumberText>
        </Row>
        <Row>
          <ItemText>{t("Est. Net")}</ItemText>
          <NumberText>
            {isNaN(Number(payment.estimated_net)) ? t("N/A") : estimatedNet}
          </NumberText>
        </Row>
      </>
    );
  } else return null;
}

/**
 * Fees include "Taxes", "Shipping", etc.
 */
export const PriceDetails = ({
  subtotal,
  total,
  fees,
  currencyCode,
  payment,
  handleValidDiscountCode,
  showDiscountCode = false,
  appliedDiscountCode,
  editableDiscountCode = false,
  onDiscountCodeErrorChange,
  buyerTenantId,
  totalDiscount,
}: {
  subtotal: string;
  total: string;
  fees: Fee[];
  currencyCode: CurrencyCode;
  payment?: Partial<Payment>;
  handleValidDiscountCode?: (discountCode: string | null) => Promise<void>;
  showDiscountCode?: boolean;
  appliedDiscountCode?: IDiscountCodesSchema | null;
  editableDiscountCode?: boolean;
  onDiscountCodeErrorChange?: (hasError: boolean) => void;
  buyerTenantId?: string;
  totalDiscount?: string;
}): JSX.Element => {
  const { t } = useTranslation();
  const methodsOfUseForm = useFormWrapper();
  const { errors } = methodsOfUseForm;

  useEffect(() => {
    const hasError = !!errors.discount_code;
    onDiscountCodeErrorChange?.(hasError);
  }, [errors.discount_code, onDiscountCodeErrorChange]);

  return (
    <Container>
      <Row>
        <ItemText>{t("Subtotal")}</ItemText>
        <NumberText data-testid={"subtotal"}>{subtotal}</NumberText>
      </Row>

      {fees.map((fee, index) => (
        <Row key={fee.id}>
          <ItemText>{fee.name}</ItemText>
          <NumberText data-testid={`fee-${index}`}>
            {
              // Just in case fee.amount is a string of "0", cast it to a
              // number for this check.
              Number(fee.amount) === 0
                ? t("Free")
                : formatPrice(fee.amount, currencyCode)
            }
          </NumberText>
        </Row>
      ))}

      {showDiscountCode && (
        <>
          <DiscountCode
            currencyCode={currencyCode}
            methodsOfUseForm={methodsOfUseForm}
            handleValidDiscountCode={handleValidDiscountCode}
            appliedCode={appliedDiscountCode}
            editable={editableDiscountCode}
            buyerTenantId={buyerTenantId}
            totalDiscount={totalDiscount}
          />
        </>
      )}

      <HorizontalSeparator />
      <Row>
        <TotalText>{t("Total")}</TotalText>
        <TotalValue data-testid={"total"}>{total}</TotalValue>
      </Row>
      {payment && (
        <StripePayments payment={payment} currencyCode={currencyCode} />
      )}
    </Container>
  );
};
